import React, { useState, useEffect } from 'react';

const StatsSection = () => {
  const [counts, setCounts] = useState({
    clients: 0,
    gifts: 0,
    years: 0,
    satisfaction: 0
  });

  const finalCounts = {
    clients: 500,
    gifts: 10000,
    years: 15,
    satisfaction: 98
  };

  useEffect(() => {
    const duration = 2000; // 2 seconds
    const steps = 60;
    const stepTime = duration / steps;

    const counters = Object.keys(finalCounts).map(key => {
      const increment = finalCounts[key] / steps;
      let currentCount = 0;
      
      return setInterval(() => {
        currentCount += increment;
        if (currentCount >= finalCounts[key]) {
          setCounts(prev => ({ ...prev, [key]: finalCounts[key] }));
          clearInterval(counters.find(c => c === this));
        } else {
          setCounts(prev => ({ ...prev, [key]: Math.floor(currentCount) }));
        }
      }, stepTime);
    });

    return () => counters.forEach(clearInterval);
  }, []);

  const stats = [
    {
      number: counts.clients,
      suffix: '+',
      title: 'Happy Clients',
      description: 'Trusted by businesses and individuals',
      icon: '🤝',
      color: 'from-primary-400 to-primary-600'
    },
    {
      number: counts.gifts,
      suffix: '+',
      title: 'Gifts Delivered',
      description: 'Creating smiles across the globe',
      icon: '🎁',
      color: 'from-accent-400 to-accent-600'
    },
    {
      number: counts.years,
      suffix: '+',
      title: 'Years Experience',
      description: 'Expertise in gifting solutions',
      icon: '🏆',
      color: 'from-luxury-400 to-luxury-600'
    },
    {
      number: counts.satisfaction,
      suffix: '%',
      title: 'Satisfaction Rate',
      description: 'Customer happiness guaranteed',
      icon: '⭐',
      color: 'from-cream-400 to-cream-600'
    }
  ];

  return (
    <section className="py-16 sm:py-20 lg:py-24 bg-gradient-to-br from-burlap-50 via-cream-50 to-burlap-100 burlap-texture px-4 sm:px-6 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23D4A574' fill-opacity='0.3'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}></div>
      </div>

      <div className="relative container mx-auto">
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="text-xs sm:text-sm font-bold text-accent-600 uppercase tracking-widest bg-accent-100 px-3 sm:px-4 py-2 rounded-full">
              Our Achievements
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl md:text-6xl font-display font-bold text-burlap-800 mb-4 sm:mb-6">
            Our Success in <span className="gradient-gold">Numbers</span>
          </h2>
          <p className="text-lg sm:text-xl text-burlap-600 max-w-4xl mx-auto leading-relaxed">
            These numbers represent our journey of creating meaningful connections through exceptional gifts
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8">
          {stats.map((stat, index) => (
            <div 
              key={index}
              className="group text-center animate-slide-up"
              style={{ animationDelay: `${index * 200}ms` }}
            >
              <div className="bg-white/80 backdrop-blur-sm rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border-2 border-cream-200 hover:border-primary-300 hover:bg-white transition-all duration-500 group-hover:scale-105 shadow-lg hover:shadow-xl">
                {/* Icon */}
                <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r ${stat.color} rounded-xl sm:rounded-2xl flex items-center justify-center text-2xl sm:text-3xl mx-auto mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                  {stat.icon}
                </div>

                {/* Number */}
                <div className="mb-3 sm:mb-4">
                  <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-burlap-800 block font-display">
                    {stat.number.toLocaleString()}{stat.suffix}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-base sm:text-lg lg:text-xl font-bold text-burlap-800 mb-1 sm:mb-2 font-display">
                  {stat.title}
                </h3>

                {/* Description */}
                <p className="text-burlap-600 text-xs sm:text-sm leading-relaxed">
                  {stat.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Additional Achievement Badges */}
        <div className="mt-12 sm:mt-16 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {[
            { icon: '🥇', title: 'Award Winner', desc: 'Best Gifting Service 2024' },
            { icon: '🌱', title: 'Eco-Friendly', desc: 'Sustainable packaging solutions' },
            { icon: '🚀', title: 'Fast Delivery', desc: 'Express shipping available' }
          ].map((badge, index) => (
            <div 
              key={index}
              className="text-center animate-fade-in"
              style={{ animationDelay: `${1000 + index * 200}ms` }}
            >
              <div className="inline-flex items-center space-x-3 bg-white/60 backdrop-blur-sm rounded-full px-4 sm:px-6 py-3 border-2 border-cream-200 hover:border-primary-300 hover:bg-white transition-all duration-300 shadow-lg">
                <span className="text-xl sm:text-2xl">{badge.icon}</span>
                <div className="text-left">
                  <p className="text-burlap-800 font-semibold text-sm">{badge.title}</p>
                  <p className="text-burlap-600 text-xs">{badge.desc}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StatsSection;
